/* eslint-disable max-lines */
import { Link, Redirect } from 'react-router-dom';
import { Trans, useTranslation } from 'react-i18next';
import Loading from '../../components/loading';
import PropTypes from 'prop-types';
import QueryString from 'query-string';
import QuotaInput from '../../components/quotas/quotaInput';
import RateFilter from '../../components/quotas/rateFilter';
import React from 'react';
import { Button, Grid, Label, Message } from 'semantic-ui-react';
import QuotasModel from '../../models/quotasModel';
import useAppContext from '../../context/useAppContext';
import { ToastContext, WeezHeader } from '@weezevent/nacre';

import { urlPrefix } from './quotaSummary';

/**
 * Renders all the zones (after {@link affectQuotasToZones}) recursively with a prefix (usually tab)
 * @param {Object} zone - The quota-affected zone to be rendered
 * @param {bool} isSubZone - Are we rendering subzones ?
 * @returns {Component} - The rendered quota
 */
export function ZonedQuota({ zone, isSubZone, currentRates, markToDelete, handleQuotaChange, currentInventory, quotas, contingent, isGA, generalAdmission }) {
    const DEFAULT_PREFIX = '- \t';

    let subs = [];
    let element = null;
    if (zone.subZones && zone.subZones.length) {
        subs = zone.subZones.map(subZone => (
            <ZonedQuota
                zone={subZone}
                isSubZone={true}
                currentRates={currentRates}
                markToDelete={markToDelete}
                handleQuotaChange={handleQuotaChange}
                currentInventory={currentInventory}
                quotas={quotas}
                key={subZone.pk}
                contingent={contingent}
                isGA={isGA}
                generalAdmission={generalAdmission}
            />
        ));
    }

    let { quota } = zone;
    let total = (
        <QuotaInput
            allQuotas={quotas}
            currentRates={currentRates}
            currentInventory={currentInventory}
            deleteQuota={quota => markToDelete(quota)}
            maximumCapacity={isGA ? generalAdmission.all : contingent.capacity}
            onChange={value => handleQuotaChange(value, quota, zone)}
            quota={quota}
            zone={zone}
        />
    );
    let booked = quota ? quota.booked : '-';
    let bookingInProgress = quota ? quota.bookingInProgress : '-';
    let allSold = total === booked;

    // XXX : prefix will probably need to change
    let bookingLabel = <Label>{`+${bookingInProgress}`}</Label>;
    let bookingTag = bookingInProgress && bookingInProgress !== '-' ? bookingLabel : null;

    let title = isSubZone ? `${DEFAULT_PREFIX}${zone.name}` : <b>{zone.name}</b>;

    let totalTag = allSold ? <Label color="grey">{total}</Label> : total;

    element = (
        <Grid.Row key={zone.pk}>
            <Grid.Column width={8}>{title}</Grid.Column>
            <Grid.Column width={4}>{isSubZone ? total : <b>{totalTag}</b>}</Grid.Column>
            <Grid.Column width={4}>
                {isSubZone ? booked : <b>{booked}</b>} {bookingTag}
            </Grid.Column>
        </Grid.Row>
    );
    return [element, subs];
}

ZonedQuota.propTypes = {
    zone: PropTypes.object.isRequired,
    isSubZone: PropTypes.bool.isRequired,
    currentRates: PropTypes.array.isRequired,
    quotas: PropTypes.array.isRequired,
    contingent: PropTypes.object.isRequired,
    isGA: PropTypes.bool.isRequired,
    currentInventory: PropTypes.object.isRequired,
    handleQuotaChange: PropTypes.func.isRequired,
    generalAdmission: PropTypes.object.isRequired,
    markToDelete: PropTypes.func.isRequired
};

/**
 * Renders all the present quotas and zones
 * @returns {React.Component[]} List of quotas/inputs
 */
export function ZonesList({ zones, currentRates, markToDelete, handleQuotaChange, currentInventory, quotas, contingent, isGA, generalAdmission }) {
    return zones.map(zone => (
        <ZonedQuota
            zone={zone}
            isSubZone={false}
            currentRates={currentRates}
            markToDelete={markToDelete}
            handleQuotaChange={handleQuotaChange}
            currentInventory={currentInventory}
            quotas={quotas}
            isGA={isGA}
            generalAdmission={generalAdmission}
            key={zone.pk}
            contingent={contingent}
        />
    ));
}

ZonesList.propTypes = {
    zones: PropTypes.array.isRequired,
    currentRates: PropTypes.array.isRequired,
    quotas: PropTypes.array.isRequired,
    contingent: PropTypes.object.isRequired,
    isGA: PropTypes.bool.isRequired,
    currentInventory: PropTypes.object.isRequired,
    handleQuotaChange: PropTypes.func.isRequired,
    generalAdmission: PropTypes.object.isRequired,
    markToDelete: PropTypes.func.isRequired
};

/**
 * Renders the quota view, which offers the possibility to filter by single or multiple rates
 */
export default function QuotaViewer({ contingent, currentInventory, editMode, generalAdmissionId, quotas, quotasSummary, rates, renderInventorySelector, zones, history, reload }) {
    const [{ event, organization }] = useAppContext();
    const [sCurrentRatesIds, setCurrentRatesIds] = React.useState(null);
    // const [sContingent, setContingent] = React.useState(contingent);
    // const [loaded, setLoaded] = React.useState(false);
    const [globalQuota, setGlobalQuota] = React.useState(null);
    const [goBack, setGoBack] = React.useState(false);
    const [changeQueryParams, setChangeQueryParams] = React.useState(false);
    const [sRates, setRates] = React.useState(null);
    const [sZones, setZones] = React.useState(null);
    const [quotasToCreate, setQuotasToCreate] = React.useState([]);
    const [quotasToUpdate, setQuotasToUpdate] = React.useState([]);
    const [quotasToDelete, setQuotasToDelete] = React.useState([]);
    const [hasRenderQuotaTotal, setRenderQuotaTotal] = React.useState(false);
    // const [needsRateUpdating, setNeedsRateUpdating] = React.useState(true);
    const isGA = !!generalAdmissionId;
    const [generalAdmission, setGeneralAdmission] = React.useState(null);
    const [sQuotas, setQuotas] = React.useState(null);
    const [contingentCapacity, setContingentCapacity] = React.useState(null);
    const toast = React.useContext(ToastContext);
    const { t } = useTranslation();

    let organizationId = organization.id;
    let eventId = event.id;

    React.useEffect(() => {
        quotasToZones(zones, quotas);
        setCurrentRatesIds(getCurrentIdsFromQueryParams());
        setRates(isGA ? null : rates);
        setZones(unflattenZones(zones));
    }, [window.location.pathname]);

    React.useEffect(() => {
        if (isGA && quotasSummary) {
            setGeneralAdmission(quotasSummary.find(summary => summary.id === parseInt(generalAdmissionId)));
            let tmpRates = getRatesForGA();
            setRates(tmpRates);
        }
        if (quotas) {
            let tmpQuotas = quotas;
            if (isGA) {
                tmpQuotas = tmpQuotas.filter(q => !q.contingent);
            }
            let globalQuota = getGlobalQuota(tmpQuotas, sCurrentRatesIds);
            setGlobalQuota(globalQuota);
            setQuotas(tmpQuotas);
        }
    }, [isGA, quotas, quotasSummary, window.location.pathname]);

    const quotasToZones = React.useCallback((zones, quotas) => {
        if (zones && quotas) {
            zones.forEach(zone => {
                zone.quotas = quotas.filter(quota => quota.zone === zone.pk);
            });
        }
    }, []);

    /**
     * Transforms the flat zones array into a parent/children tree
     * @param {[]} zones - The flat array of zones to transform
     * @returns {[]} - Tree of zones
     */
    const unflattenZones = React.useCallback(zones => {
        let result = [];
        zones.forEach(zone => {
            if (!zone.parent) {
                result.push(zone);
                return;
            }

            let parent = zones.find(zoneToFind => zoneToFind.pk === zone.parent);
            if (!parent) {
                result.push(zone);
                return;
            }
            parent.subZones = parent.subZones || [];

            // memory check
            if (!parent.subZones.find(subzone => subzone.pk === zone.pk)) {
                parent.subZones.push(zone);
                parent.subZones = parent.subZones.sort(sortAlphabetically);
            }
        });
        return result.sort(sortAlphabetically);
    }, []);

    const getCurrentIdsFromQueryParams = React.useCallback(() => {
        let currentRatesIds = [];

        if (window.location.search) {
            let { selectedRates } = QueryString.parse(window.location.search);
            currentRatesIds = selectedRates.split(',').map(val => parseInt(val));
        }
        return currentRatesIds.filter(r => r);
    }, []);

    const getRatesForGA = React.useCallback(() => {
        let inventoryId = currentInventory.id();
        let tmpGeneralAdmission = generalAdmission;
        if (!generalAdmission) {
            tmpGeneralAdmission = quotasSummary.find(summary => summary.id === parseInt(generalAdmissionId));
            setGeneralAdmission(tmpGeneralAdmission);
        }

        let tmpRates = rates.filter(rate => {
            if (rate.inventories.indexOf(inventoryId) === -1) {
                return false;
            }

            const rateCategoryId = rate.category || rate.rate_template?.category;
            if (rateCategoryId !== tmpGeneralAdmission.category) {
                return false;
            }

            if (rate.contingents.indexOf(tmpGeneralAdmission.id) > -1 || rate.contingents.length === 0) {
                return true;
            }

            return false;
        });

        return tmpRates;
    }, [generalAdmission, quotasSummary, generalAdmissionId, rates]);

    const getGlobalQuota = React.useCallback(
        (quotas, currentRatesIds) => {
            if (!currentRatesIds || !currentRatesIds.length) {
                return null;
            }

            let globalQuota = null;

            const ratesMatch = quota => {
                return quota.rates.map(rate => (currentRatesIds.indexOf(rate) > -1 ? 1 : 0)).reduce((sum, value) => sum + value, 0) === currentRatesIds.length;
            };

            if (isGA) {
                let generalAdmissionTmp = quotasSummary.find(summary => summary.id === parseInt(generalAdmissionId));
                if (!generalAdmissionTmp) {
                    return null;
                }

                globalQuota = quotas.find(quota => !quota.contingent && quota.zone === generalAdmissionTmp.zone && ratesMatch(quota));
            } else {
                globalQuota = quotas.find(quota => !quota.zone && ratesMatch(quota));
            }

            return globalQuota;
        },
        [isGA, generalAdmission]
    );

    /**
     * Sorts an array alphabetically
     * @param {Object} item1 - First item to sort
     * @param {Object} item2 - Second item to sort
     * @returns {number} Result of alphabetical sort
     */
    const sortAlphabetically = React.useCallback((item1, item2) => {
        if (item1.name < item2.name) return -1;
        if (item1.name > item2.name) return 1;
        return 0;
    }, []);

    /**
     * Affects all quotas to the corresponding zones to allow for simpler rendering
     * @param {Object[]} zones - The zones to affect the quotas to
     * @param {Object[]} quotas - The quotas to affect to each zone
     * @returns {Object[]} - Zones with affected quotas
     */
    const affectQuotasToZones = React.useCallback((zones, quotas) => {
        for (let zone of zones) {
            zone.quota = quotas.find(quota => quota.zone === zone.pk);
            if (zone.subZones) {
                affectQuotasToZones(zone.subZones, quotas);
            }
        }
        return zones;
    }, []);

    /**
     * Filters the zones by every present quota, will be used with rates.
     * @param {Object[]} rates - The rates used to filter the zones
     * @returns {Object[]} - Filtered zones
     */
    const filterZones = React.useCallback(
        rates => {
            let categories = new Set();
            rates.forEach(rate => {
                categories.add(rate.category);
            });
            let inventoryId = currentInventory.id();

            return zones.filter(zone => {
                if (!zone.categories(inventoryId) || !zone.categories(inventoryId).length) {
                    return false;
                }

                for (let zoneCategory of zone.categories(inventoryId)) {
                    if (categories.has(zoneCategory)) {
                        return true;
                    }
                }

                return false;
            });
        },
        [currentInventory, zones]
    );

    /**
     * Calculates displayable data
     * @param {Object[]} quotas - The filtered quotas
     * @param {Object[]} zones - The quota-affected zones
     * @param {string[]} currentRatesIds - Ids of currently selected rates
     */
    const calculateData = React.useCallback(
        (quotas, zones, currentRatesIds) => {
            let tmpContingentCapacity = isGA ? 0 : contingent.capacity;

            let tmpGlobalQuota = getGlobalQuota(quotas, currentRatesIds);

            if (!tmpGlobalQuota) {
                tmpGlobalQuota = new QuotasModel({
                    total: null,
                    rates: currentRatesIds,
                    contingent: isGA ? null : contingent.id
                });
            }
            setGlobalQuota(tmpGlobalQuota);
            setRenderQuotaTotal(currentRatesIds && currentRatesIds.length);
            setZones(zones);
            setQuotas(quotas);
            setCurrentRatesIds(currentRatesIds);
            setContingentCapacity(tmpContingentCapacity);
        },
        [isGA, contingent, quotas]
    );

    /**
     * Filters all the data by the selected rates on {@link RateFilter}
     * @param {Object[]} rates - The rates used to filter all quotas
     * @returns {undefined} - No return
     */
    const filterByRates = React.useCallback(
        prates => {
            let tmpQuotas = prates.length
                ? quotas.filter(quota => {
                      // if quota has no rates
                      if (!quota.rates || !quota.rates.length) {
                          return false;
                      }

                      // if quota has rates they must all be the same
                      if (quota.rates.length === prates.length) {
                          let ct = 0;
                          for (let rate of prates) {
                              if (quota.rates.indexOf(rate) > -1) {
                                  ct++;
                              }
                          }
                          return ct === prates.length;
                      }

                      return false;
                  })
                : [];

            // display only zones that share a category
            let filteredRates = rates.filter(rate => prates.indexOf(rate.id) > -1);

            let zones = filterZones(filteredRates);
            // unflatten zones to put children where they belong
            zones = unflattenZones(zones);

            // put quotas in zones to display
            zones = affectQuotasToZones(zones, tmpQuotas);
            calculateData(tmpQuotas, zones, prates);
        },
        [quotas, calculateData, affectQuotasToZones, filterZones, unflattenZones, rates]
    );

    /**
     * Handles the changing of the value of a quota
     * @param {number} newValue - New value for quota
     * @param {Quota} quota - The quota for which the value has changed
     * @param {Zone} zone - The zone linked with the current quota
     */
    const handleQuotaChange = React.useCallback(
        (newValue, quota, zone) => {
            let newQuotasToCreate = quotasToCreate.slice(0);
            let newQuotasToUpdate = quotasToUpdate.slice(0);

            if (quota) {
                // check if quota changed value twice
                let existingQuotaIndex = newQuotasToUpdate.findIndex(quotaToSave => quota.id === quotaToSave.id);

                if (existingQuotaIndex > -1) {
                    let existingQuota = quotasToUpdate[existingQuotaIndex];
                    existingQuota.setTotal(newValue);
                    newQuotasToUpdate.splice(existingQuotaIndex, 1, existingQuota);
                } else {
                    quota.setTotal(newValue);
                    newQuotasToUpdate.push(quota);
                }
            } else {
                let exists = quotasToCreate.find(quota => {
                    let currentRates = sCurrentRatesIds.slice(0).sort();
                    return JSON.stringify(quota.rates.sort()) === JSON.stringify(currentRates) && quota.zone === zone.pk;
                });

                if (exists) {
                    exists.setTotal(newValue);
                } else {
                    let newQuota = new QuotasModel({
                        zone: zone.pk,
                        rates: sCurrentRatesIds,
                        total: newValue,
                        contingent: isGA ? null : contingent.id
                    });
                    newQuotasToCreate.push(newQuota);
                }
            }
            setQuotasToCreate(newQuotasToCreate);
            setQuotasToUpdate(newQuotasToUpdate);
        },
        [quotasToCreate, quotasToDelete, sCurrentRatesIds, quotasToUpdate]
    );

    /**
     * Add a quota to deletion list
     * @param {Quota} quota - Quota to be deleted
     */
    const markToDelete = React.useCallback(
        quotaToDelete => {
            if (!quotaToDelete) {
                return;
            }

            let exists = quotasToDelete.find(quota => quota.id === quotaToDelete.id);
            if (!exists) {
                let tmpQuotasToDelete = quotasToDelete.slice(0);
                tmpQuotasToDelete.push(quotaToDelete);

                // XXX: if the user is a bot this could go wrong
                // since he could click faster than setState updates the array
                setQuotasToDelete(tmpQuotasToDelete);
            }
        },
        [quotasToDelete]
    );

    const saveQuotas = function(objs) {
        let promise = null,
            inventoryId = currentInventory.id();

        if (objs instanceof Array) {
            if (objs.every(obj => obj.pk !== null)) {
                objs.forEach(obj => {
                    promise = obj.save({ organizationId, eventId, inventoryId });
                });
            } else {
                promise = QuotasModel.save(objs, { organizationId, eventId, inventoryId });
            }
        } else {
            if (objs.pk !== null) {
                promise = objs.save({ organizationId, eventId, inventoryId });
            } else {
                promise = QuotasModel.save(objs, { organizationId, eventId, inventoryId });
            }
        }

        if (promise) {
            promise
                .then(() => {
                    toast.success(t('ticket.quotas.edition_success'));
                })
                .catch(e => {
                    toast.error(t('ticket.quotas.edition_error'));
                });
        }
    };
    /**
     * Save quotas to network
     * @returns {undefined}
     */
    const saveQuotasCallback = React.useCallback(() => {
        if (quotasToUpdate) {
            saveQuotas(quotasToUpdate);
        }
        if (quotasToCreate) {
            saveQuotas(quotasToCreate);
        }
        if (globalQuota) {
            if (globalQuota.pk !== null) {
                if (globalQuota.rates_list) {
                    delete globalQuota.rates_list;
                    delete globalQuota.zones_list;
                }
            }
            saveQuotas(globalQuota);
        }

        quotasToDelete.forEach(quota => {
            deleteQuota(quota);
        });
        setGoBack(true);
    }, [quotasToCreate, quotasToUpdate, globalQuota, quotasToDelete]);

    /**
     * Delete a given quota (as callback on button)
     */
    const deleteQuota = React.useCallback(
        quota => {
            quota
                .delete({
                    id: quota.id,
                    eventId,
                    organizationId,
                    inventoryId: currentInventory.id()
                })
                .catch(e => {});
        },
        [currentInventory]
    );

    const prefix = urlPrefix(eventId, organizationId);

    if (goBack) {
        history.push(`${prefix}/seating/quotas/inventory/${currentInventory.id()}`);
        reload();
    }

    if (changeQueryParams) {
        setChangeQueryParams(false);

        if (isGA) {
            return (
                <Redirect
                    to={`${prefix}/seating/quotas/inventory/${currentInventory.id()}/generaladmission/${generalAdmissionId}/edit?selectedRates=${sCurrentRatesIds.join(',')}${
                        editMode ? '&editMode=true' : ''
                    }`}
                />
            );
        }

        return (
            <Redirect
                to={`${prefix}/seating/quotas/inventory/${currentInventory.id()}/contingent/${contingent.id}/edit?selectedRates=${sCurrentRatesIds.join(',')}${
                    editMode ? '&editMode=true' : ''
                }`}
            />
        );
    }

    if (!sRates || !sQuotas || (isGA && !quotasSummary)) {
        return <Loading />;
    }

    let currentRates = rates.filter(rate => sCurrentRatesIds.indexOf(rate.id) > -1);

    return (
        <div>
            <div className="header">
                <WeezHeader
                    title={
                        isGA ? (
                            <React.Fragment>
                                <Trans i18nKey="quotas_generalAdmissionQuotaViewTitle">Quotas for General Admission</Trans>
                                <span className="contingent-name-title"> {generalAdmission ? generalAdmission.name : null}</span>
                            </React.Fragment>
                        ) : (
                            <React.Fragment>
                                <Trans i18nKey="contingentQuotaViewTitle">Quotas for contingent</Trans>
                                <span className="contingent-name-title"> {contingent.name}</span>
                            </React.Fragment>
                        )
                    }
                />
                {renderInventorySelector()}
                <p></p>
            </div>
            <span>
                <Link to={`${prefix}/seating/quotas/inventory/${currentInventory.id()}`}>
                    <Trans i18nKey="quotasList">Quotas list</Trans>
                </Link>
            </span>
            <div>
                <br />
                <span className="form-title primary">
                    {isGA ? (
                        <Trans i18nKey="quotaGATotalQuota" count={contingentCapacity}>
                            Total quota for general admission area: %count%
                        </Trans>
                    ) : (
                        <Trans i18nKey="quotaContingentTotalQuota" count={contingentCapacity}>
                            Total quota for contingent: %count%
                        </Trans>
                    )}
                </span>
            </div>
            <div>
                <br />
                <span className="form-title">
                    1.&nbsp;
                    <Trans i18nKey="quotas_selectRates">Select one or more rates</Trans>
                </span>

                <RateFilter onChange={id => filterByRates(id)} disabled={editMode} defaultValue={sCurrentRatesIds} rates={sRates} />
            </div>
            {!isGA && (
                <div>
                    <br />
                    <span className="form-title">
                        2.&nbsp;
                        <Trans i18nKey="quotas_quotaDetails">Quotas detail for selected rates and every zone</Trans>
                    </span>
                    {!currentRates || !currentRates.length ? (
                        <Message>
                            <Trans i18nKey="quotas_selectRatesToEdit">Please select one or more rates to edit quotas</Trans>
                        </Message>
                    ) : (
                        <Grid celled>
                            <Grid.Row>
                                <Grid.Column width={8}>
                                    <Trans i18nKey="zone" count={2}>
                                        Zones
                                    </Trans>
                                </Grid.Column>
                                <Grid.Column width={4}>
                                    <Trans i18nKey="quotaTotal">Quota total</Trans>
                                </Grid.Column>
                                <Grid.Column width={4}>
                                    <Trans i18nKey="sale" count={2}>
                                        Ventes
                                    </Trans>
                                </Grid.Column>
                            </Grid.Row>
                            <ZonesList
                                zones={sZones}
                                currentRates={currentRates}
                                markToDelete={markToDelete}
                                handleQuotaChange={handleQuotaChange}
                                currentInventory={currentInventory}
                                quotas={quotas}
                                isGA={isGA}
                                generalAdmission={generalAdmission}
                                contingent={contingent}
                            />
                        </Grid>
                    )}
                </div>
            )}
            <div>
                <br />
                <div>
                    <span className="form-title">
                        {isGA ? '2' : '3'}.&nbsp;
                        {isGA ? (
                            <Trans i18nKey="quotas_totalForGA">Total quota for this general admission area</Trans>
                        ) : (
                            <Trans i18nKey="quotas_totalForContingent">Quota total for this contingent</Trans>
                        )}
                    </span>
                </div>
                {!hasRenderQuotaTotal ? (
                    '-'
                ) : (
                    <QuotaInput
                        allQuotas={sQuotas}
                        currentRates={currentRates}
                        currentInventory={currentInventory}
                        deleteQuota={quota => markToDelete(quota)}
                        maximumCapacity={isGA ? generalAdmission.all : contingent.capacity}
                        quota={globalQuota}
                        onChange={value => {
                            let tmpGlobalQuota = globalQuota;
                            tmpGlobalQuota.setTotal(value);

                            if (isGA) {
                                tmpGlobalQuota.zone = generalAdmission.zone;
                            }
                            setGlobalQuota(tmpGlobalQuota);
                        }}
                    />
                )}
            </div>
            <div className="save-quotas">
                <Button onClick={() => setGoBack(true)}>
                    <Trans i18nKey="ticket.common.action.cancel">Return</Trans>
                </Button>
                <Button onClick={() => saveQuotasCallback()} primary>
                    <Trans i18nKey="ticket.common.action.save">Save</Trans>
                </Button>
            </div>
        </div>
    );
}

QuotaViewer.propTypes = {
    contingent: PropTypes.object,
    currentInventory: PropTypes.object.isRequired,
    editMode: PropTypes.bool,
    event: PropTypes.object.isRequired,
    generalAdmissionId: PropTypes.number,
    quotas: PropTypes.array,
    quotasSummary: PropTypes.array,
    rates: PropTypes.array.isRequired,
    renderInventorySelector: PropTypes.func.isRequired,
    zones: PropTypes.array.isRequired,
    history: PropTypes.object.isRequired,
    reload: PropTypes.func.isRequired
};

QuotaViewer.defaultProps = {
    contingent: null,
    editMode: false
};
